import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// =========================================================================
// Toast — the demo's `#toast` element, as a provider. Fork-owned (zone C).
//
// Deliberately minimal: one line of text, auto-dismiss, no actions, no
// variants. The demo uses it for exactly one job — confirming that something
// just happened off-screen (an approval flipped, the persona changed). Adding
// buttons or severities would invite it to replace the drawer.
//
// Rendering lives in ToastHost, mounted once in app/layout.tsx.
// =========================================================================

/** Milliseconds a toast stays up. Matches the demo's dismissal timing. */
export const TOAST_DURATION_MS = 3200;

export type Toast = { id: number; message: string };

type ToastContextValue = {
  toasts: Toast[];
  /** Queue a message. Returns the toast id so a caller can dismiss early. */
  show: (message: string) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message: string) => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { id, message }]);
      const handle = window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
        timers.current.delete(id);
      }, TOAST_DURATION_MS);
      timers.current.set(id, handle);
      return id;
    },
    [],
  );

  // Clear pending timers on unmount so a fast route change can't fire into a
  // dead component.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((handle) => window.clearTimeout(handle));
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, show, dismiss }),
    [toasts, show, dismiss],
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
